import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, Undo2, Calculator, Send, CheckCircle2, XCircle, RotateCcw, Trash, FileSpreadsheet, Printer, Download, Users,
    ScrollText, Check, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Search, AlertTriangle, Eye, Loader2, Rows3, LayoutList, Layers3, Factory,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { PAYROLL_STATUS_LABELS, formatPayrollCode, getPayrollDateRange } from '../payroll/core/domain';
import { PAYROLL_EXCLUSION_REASONS } from '../payroll/core/templateResolver';
import { buildDetailRow, buildPeriodCalculation, buildWorkbenchGroups, isPeriodEditable } from '../payroll/core/periodWorkbench';
import {
    applyPeriodEmployeeRefresh, approvePeriod, confirmPeriodCalculation, createPeriod, deletePeriod, excludeEmployeeFromTemplate,
    listPeriods, listTemplates, loadPeriodAudit, loadPeriodWorkbench, loadProductivityIntoPeriod, previewPeriodCalculation,
    previewPeriodDraft, previewPeriodEmployeeRefresh, rejectPeriod, reopenPeriod, savePeriodInputs, savePeriodNote, submitPeriod,
} from '../payroll/data/periodActions';
import { exportPayrollWorkbook } from '../payroll/data/exportPayroll';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage, currentPayrollUser } from '../payroll/data/permissions';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import WorkbenchMatrix, { SORT_OPTIONS, SOURCE_BADGES, formatCellValue, sortRows } from '../payroll/components/WorkbenchMatrix';
import {
    AuditModal, CalculationPreviewModal, ChangeTemplatesModal, ConfirmWarningsModal, EmployeeDetailModal, ImportInputsModal,
    ReasonModal,
} from '../payroll/components/PeriodDialogs';
import {
    AccessDenied, Badge, ConfirmDialog, MessageList, Spinner, buttonPrimary, buttonSecondary, formatDateTime, formatMoney,
    inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass,
} from '../payroll/components/ui';
import { STATUS_TONES } from './BangCongLuong';

const SUMMARY_TAB = '__tong_hop__';
const NO_TEAM = '__chua_phan_to__';
const LAYOUT_KEY = 'payroll-matrix-layout';

function readLayout() {
    try { return window.localStorage.getItem(LAYOUT_KEY) === 'vertical' ? 'vertical' : 'horizontal'; } catch { return 'horizontal'; }
}
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

function WorkflowSteps({ period, stale }) {
    const calculated = Boolean(period.CALCULATED_AT) && !stale;
    const current = period.TRANG_THAI === 'DA_DUYET' ? 4 : period.TRANG_THAI === 'CHO_DUYET' ? 3 : calculated ? 3 : 1;
    return (
        <span className="flex flex-wrap items-center gap-1">
            {['Nhập liệu', 'Chốt số', 'Duyệt'].map((label, index) => {
                const step = index + 1;
                const done = step < current;
                const active = step === current;
                return (
                    <span key={label} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${done ? 'bg-green-100 text-green-800' : active ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'}`}>
                        {done ? <Check className="w-3 h-3" /> : step}. {label}
                    </span>
                );
            })}
        </span>
    );
}

// Số đang gõ (chuỗi số chuẩn từ ô nhập, vd "25.5") thành số để tính; ô trống là xóa số đã nhập, ô sai được đánh dấu.
function parsePending(pendingRaw) {
    const inputs = {};
    const invalid = new Set();
    for (const [soHd, codes] of Object.entries(pendingRaw)) {
        for (const [code, raw] of Object.entries(codes)) {
            if (String(raw).trim() === '') {
                inputs[soHd] = { ...(inputs[soHd] ?? {}), [code]: null };
                continue;
            }
            const value = parseSheetNumber(raw, null);
            if (value === null || Number.isNaN(value)) invalid.add(`${soHd}|${code}`);
            else inputs[soHd] = { ...(inputs[soHd] ?? {}), [code]: value };
        }
    }
    return { inputs, invalid };
}

function MoreMenu({ items }) {
    const [open, setOpen] = useState(false);
    const visible = items.filter(Boolean);
    if (!visible.length) return null;
    return (
        <div className="relative">
            <button type="button" className={buttonSecondary} onClick={() => setOpen(!open)} title="Thao tác khác" aria-label="Thao tác khác"><MoreHorizontal className="w-4 h-4" /></button>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-40 py-1">
                        {visible.map(({ label, icon: Icon, onClick, danger }) => (
                            <button key={label} type="button" onClick={() => { setOpen(false); onClick(); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${danger ? 'text-red-600' : 'text-gray-700'}`}>
                                <Icon className="w-4 h-4" />{label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function PanelSection({ title, children }) {
    return (
        <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</p>
            {children}
        </div>
    );
}

function SummaryTable({ groups, sort, onView }) {
    const value = (row, code) => row.cells[code]?.value ?? row.result?.values?.[code] ?? 0;
    const rows = sortRows(
        groups.flatMap((group) => group.rows.map((row) => ({ ...row, templateName: group.templateName })))
            .sort((left, right) => String(left.to).localeCompare(String(right.to), 'vi', { numeric: true }) || String(left.hoTen).localeCompare(String(right.hoTen), 'vi')),
        sort, (row) => Number(value(row, 'TCHI')) || 0,
    );
    const total = (code) => rows.reduce((sum, row) => sum + (Number(value(row, code)) || 0), 0);
    return (
        <div className="overflow-auto border border-gray-200 rounded-lg max-h-[68vh]">
            <table className={tableClass}>
                <thead className={`${theadClass} sticky top-0 z-10`}>
                    <tr>
                        <th className={`${thClass} text-center`}>STT</th><th className={thClass}>Nhân sự</th><th className={thClass}>Tổ</th><th className={thClass}>Mẫu</th>
                        <th className={`${thClass} text-right`}>Tổng cộng</th><th className={`${thClass} text-right`}>Trừ ứng</th><th className={`${thClass} text-right`}>BH NLĐ</th>
                        <th className={`${thClass} text-right`}>Thuế TNCN</th><th className={`${thClass} text-right`}>Thực chi</th><th className={`${thClass} text-center`}>Xem</th>
                    </tr>
                </thead>
                <tbody className={tbodyClass}>
                    {rows.map((row, index) => (
                        <tr key={row.soHd} className="hover:bg-gray-50">
                            <td className={`${tdClass} text-center text-gray-400`}>{index + 1}</td>
                            <td className={tdClass}>
                                <div className="font-medium text-gray-900 flex items-center gap-1">{row.warnings.length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />}{row.hoTen}</div>
                                <div className="text-xs text-gray-500">{row.soHd}</div>
                            </td>
                            <td className={tdClass}>{row.to || <span className="text-gray-400">Chưa phân tổ</span>}</td>
                            <td className={tdClass}>{row.templateName}</td>
                            {['TC', 'UNG', 'BH_NLD', 'TNCN'].map((code) => <td key={code} className={`${tdClass} text-right whitespace-nowrap`}>{formatCellValue(value(row, code), 'TIEN')}</td>)}
                            <td className={`${tdClass} text-right whitespace-nowrap font-bold text-green-800 bg-green-50`}>{formatCellValue(value(row, 'TCHI'), 'TIEN')}</td>
                            <td className={`${tdClass} text-center`}><button type="button" onClick={() => onView(row)} className="p-1.5 rounded-full text-indigo-600 hover:bg-indigo-50" aria-label={`Xem ${row.hoTen}`}><Eye className="w-4 h-4" /></button></td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="sticky bottom-0">
                    <tr className="bg-gray-100 font-semibold text-gray-900">
                        <td className="px-3 py-2" /><td className="px-3 py-2" colSpan={3}>TỔNG ({rows.length})</td>
                        {['TC', 'UNG', 'BH_NLD', 'TNCN', 'TCHI'].map((code) => <td key={code} className="px-3 py-2 text-right whitespace-nowrap">{formatCellValue(total(code), 'TIEN')}</td>)}
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

const BangCongLuongChiTiet = ({ createMode = false }) => {
    const { maKy } = useParams();
    const navigate = useNavigate();
    const canView = canViewPayrollPage(PAYROLL_PATHS.periods);
    const canManage = canManagePayroll();
    const user = currentPayrollUser().username;
    const today = new Date();

    // Kỳ đã có
    const [wb, setWb] = useState(null);
    const [loadError, setLoadError] = useState(null);
    // Tạo kỳ
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [templateList, setTemplateList] = useState(null);
    const [selected, setSelected] = useState([]);
    const [templateSearch, setTemplateSearch] = useState('');
    const [note, setNote] = useState('');
    const [existingCodes, setExistingCodes] = useState(new Set());
    const [draft, setDraft] = useState(null);
    const [draftLoading, setDraftLoading] = useState(false);
    const [draftVersion, setDraftVersion] = useState(0);
    // Chung
    const [pendingRaw, setPendingRaw] = useState({});
    const [activeTab, setActiveTab] = useState(null);
    const [search, setSearch] = useState('');
    const [teamFilter, setTeamFilter] = useState('ALL');
    const [onlyWarnings, setOnlyWarnings] = useState(false);
    const [panelOpen, setPanelOpen] = useState(true);
    const [layout, setLayout] = useState(readLayout);
    const [showOnlyInputs, setShowOnlyInputs] = useState(false);
    const [sort, setSort] = useState('default');
    const [noteDraft, setNoteDraft] = useState(null);
    const [busy, setBusy] = useState('');
    const [dialog, setDialog] = useState(null);

    const reload = useCallback(async () => {
        if (createMode) return;
        try {
            setLoadError(null);
            setWb(await loadPeriodWorkbench(maKy));
        } catch (error) {
            console.error('Lỗi tải kỳ lương:', error);
            setLoadError(error.message);
        }
    }, [createMode, maKy]);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    useEffect(() => {
        if (!canView || !createMode) return;
        Promise.all([listTemplates(), listPeriods()])
            .then(([templates, periods]) => {
                const active = templates.filter((template) => template.HOAT_DONG);
                setTemplateList(active);
                setSelected(active.map((template) => template.MA_MAU));
                setExistingCodes(new Set(periods.map((period) => period.MA_KY)));
            })
            .catch(() => toast.error('Không tải được danh sách mẫu'));
    }, [canView, createMode]);

    // Xem trước khi tạo kỳ: chạy lại mỗi khi đổi tháng, năm, mẫu (chờ người dùng ngừng bấm một nhịp).
    useEffect(() => {
        if (!createMode || !templateList) return undefined;
        if (!selected.length) { setDraft(null); return undefined; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setDraftLoading(true);
                const result = await previewPeriodDraft(month, year, selected);
                if (!cancelled) setDraft(result);
            } catch (error) {
                if (!cancelled) toast.error(error.message);
            } finally {
                if (!cancelled) setDraftLoading(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [createMode, templateList, month, year, selected, draftVersion]);

    const { inputs: pendingInputs, invalid: invalidCells } = useMemo(() => parsePending(pendingRaw), [pendingRaw]);
    const dirtyCount = Object.values(pendingRaw).reduce((sum, codes) => sum + Object.keys(codes).length, 0);

    useEffect(() => {
        if (!dirtyCount) return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirtyCount]);

    // Kỳ đang xem ở dạng chung cho cả hai chế độ.
    const view = useMemo(() => {
        if (createMode) {
            if (!draft) return null;
            const code = formatPayrollCode(month, year);
            const range = getPayrollDateRange(month, year);
            return {
                period: { MA_KY: code, THANG: month, NAM: year, TU_NGAY: range.from, DEN_NGAY: startOfDay(range.to), TRANG_THAI: 'NHAP', MAU_IDS: selected, GHI_CHU: note },
                details: draft.resolved.employees.map((employee) => buildDetailRow(code, employee, draft.ref.employees)),
                ref: draft.ref,
                templates: draft.ref.templates,
            };
        }
        return wb ? { period: wb.period, details: wb.details, ref: wb.ref, templates: wb.templates } : null;
    }, [createMode, draft, month, year, selected, note, wb]);

    const periodEditable = view ? isPeriodEditable(view.period) : false;
    const editable = periodEditable && canManage;

    const live = useMemo(() => {
        if (!view) return null;
        try {
            const result = buildPeriodCalculation({ ...view, pending: { inputs: pendingInputs } });
            const groups = buildWorkbenchGroups({
                period: view.period, details: view.details, calculation: result.calculation, compile: result.compile, templateById: result.templateById,
                variableByCode: view.ref.variableByCode, pendingInputs, editable, useSnapshot: !periodEditable,
            });
            return { ...result, groups };
        } catch (error) {
            return { error: error.message, groups: [] };
        }
    }, [view, pendingInputs, editable, periodEditable]);

    useEffect(() => {
        if (live && activeTab && activeTab !== SUMMARY_TAB && !live.groups.some((group) => group.templateId === activeTab)) setActiveTab(null);
    }, [live, activeTab]);

    if (!canView) return <AccessDenied />;
    if (loadError) {
        return (
            <div className="p-3 bg-gray-50 min-h-screen">
                <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 space-y-4">
                    <button className={buttonSecondary} onClick={() => navigate('/bangcongluong')}><ArrowLeft className="w-4 h-4" />Danh sách</button>
                    <MessageList items={[loadError]} />
                </div>
            </div>
        );
    }

    const period = view?.period;
    const stale = Boolean(wb?.stale);
    const groups = live?.groups ?? [];
    const teams = view ? [...new Set(view.details.map((detail) => detail.TO_SNAPSHOT).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true })) : [];
    const hasNoTeam = Boolean(view?.details.some((detail) => !detail.TO_SNAPSHOT));
    const filterRows = (rows) => rows.filter((row) => matchesSearch(`${row.hoTen} ${row.soHd} ${row.to}`, search)
        && (teamFilter === 'ALL' || (teamFilter === NO_TEAM ? !row.to : row.to === teamFilter))
        && (!onlyWarnings || row.warnings.length > 0));
    const filteredGroups = groups.map((group) => ({ ...group, rows: filterRows(group.rows) }));
    // Mặc định mở bảng nhập của mẫu đầu tiên; tab Tổng hợp xem lại cả kỳ.
    const currentTab = activeTab ?? groups[0]?.templateId ?? SUMMARY_TAB;
    const activeGroup = filteredGroups.find((group) => group.templateId === currentTab);
    const activeRows = activeGroup ? sortRows(activeGroup.rows, sort, (row) => Number(row.cells.TCHI?.value ?? row.result?.values?.TCHI) || 0) : [];
    const activeUsesProductivity = Boolean(activeGroup?.columns.some((column) => column.source === 'NANG_SUAT'));
    const legendSources = [...new Set((activeGroup ? [activeGroup] : groups).flatMap((group) => group.columns.map((column) => column.source)))];
    const warningRows = groups.reduce((sum, group) => sum + group.rows.filter((row) => row.warnings.length).length, 0);
    const summary = periodEditable && live?.calculation
        ? live.calculation.summary
        : { totalGross: period?.TONG_LUONG, totalAdvance: period?.TONG_UNG, totalInsurance: period?.TONG_BHXH, totalNet: period?.TONG_THUC_CHI };
    const detailBySoHd = new Map((view?.details ?? []).map((detail) => [detail.NHAN_SU_ID, detail]));
    const usedTemplates = groups.map((group) => ({ id: group.templateId, name: group.templateName, count: group.rows.length, template: view.templates.find((template) => template.ID === group.templateId) }));

    // Chạy một thao tác: tự lưu số đang sửa trước (kỳ đã có), báo lỗi bằng toast, tải lại kỳ.
    const run = async (label, action, { saveFirst = true, reloadAfter = true } = {}) => {
        if (invalidCells.size) {
            toast.error('Có ô nhập không phải số (khung đỏ), sửa trước khi tiếp tục.');
            return undefined;
        }
        try {
            setBusy(label);
            if (!createMode && saveFirst && dirtyCount) {
                await savePeriodInputs(maKy, pendingInputs);
                setPendingRaw({});
            }
            const result = await action();
            if (reloadAfter) await reload();
            return result;
        } catch (error) {
            toast.error(error.message);
            return undefined;
        } finally {
            setBusy('');
        }
    };

    const handleCreate = async () => {
        const inputs = Object.fromEntries(Object.entries(pendingInputs).map(([soHd, codes]) => [soHd, Object.fromEntries(Object.entries(codes).filter(([, value]) => value !== null))]));
        const created = await run('Đang tạo bảng lương...', () => createPeriod({ month, year, templateCodes: selected, note, inputs }), { reloadAfter: false });
        if (created) {
            setPendingRaw({});
            toast.success(`Đã tạo bảng lương ${created.MA_KY}`);
            navigate(`/bangcongluong/${created.MA_KY}`);
        }
    };

    const handleSave = () => run('Đang lưu...', async () => {
        const count = await savePeriodInputs(maKy, pendingInputs);
        setPendingRaw({});
        toast.success(`Đã lưu số nhập của ${count} nhân sự`);
    }, { saveFirst: false });

    const handleCalculate = async () => {
        const preview = await run('Đang nạp năng suất và tính lại...', () => previewPeriodCalculation(maKy), { reloadAfter: false });
        if (preview) setDialog({ type: 'calculate', preview });
    };

    const handleSubmit = async () => {
        const result = await run('Đang gửi duyệt...', () => submitPeriod(maKy, false));
        if (result?.requiresConfirmation) setDialog({ type: 'confirmWarnings', warnings: result.warnings });
        else if (result?.submitted) toast.success('Đã gửi kỳ lương chờ duyệt');
    };

    const handleChangeTemplates = async () => {
        const templates = await run('Đang tải mẫu bảng lương...', () => listTemplates(), { saveFirst: false, reloadAfter: false });
        if (templates) setDialog({ type: 'templates', templates: templates.filter((template) => template.HOAT_DONG || period.MAU_IDS?.includes(template.MA_MAU)) });
    };

    const handleLoadProductivity = async () => {
        const result = await run('Đang nạp năng suất...', () => loadProductivityIntoPeriod(maKy));
        if (result) {
            toast.success(`Đã nạp năng suất: ${result.people} người, ${formatMoney(result.total)} đ. Bấm Chốt số để ghi số chính thức.`);
            if (result.unmatchedCount) toast.warning(`${result.unmatchedCount} dòng năng suất chưa có người nhận (${formatMoney(result.unmatchedAmount)} đ).`);
        }
    };

    const handleSaveNote = async () => {
        const ok = await run('Đang lưu ghi chú...', async () => { await savePeriodNote(maKy, noteDraft); return true; }, { saveFirst: false });
        if (ok) { setNoteDraft(null); toast.success('Đã lưu ghi chú'); }
    };

    const changeLayout = (value) => {
        setLayout(value);
        try { window.localStorage.setItem(LAYOUT_KEY, value); } catch { /* trình duyệt chặn lưu thì dùng mặc định */ }
    };

    const handleShowAudit = async () => {
        const rows = await run('Đang tải nhật ký...', () => loadPeriodAudit(maKy), { saveFirst: false, reloadAfter: false });
        if (rows) setDialog({ type: 'audit', rows });
    };

    const handleExcludeFromTemplate = async (templateId, templateName, employee) => {
        const ok = await run(`Đang gỡ ${employee.employeeName} khỏi ${templateName}...`, async () => { await excludeEmployeeFromTemplate(templateId, employee.employeeId); return true; }, { reloadAfter: false });
        if (ok) { toast.success(`Đã gỡ ${employee.employeeName} khỏi mẫu ${templateName}`); setDraftVersion((value) => value + 1); }
    };

    const handleBack = () => {
        if (dirtyCount) setDialog({ type: 'leave' });
        else navigate('/bangcongluong');
    };

    const handleInputChange = (soHd, code, raw) => setPendingRaw((prev) => ({ ...prev, [soHd]: { ...(prev[soHd] ?? {}), [code]: raw } }));

    const applyImportedChanges = (changes) => {
        setPendingRaw((prev) => {
            const next = { ...prev };
            for (const [soHd, codes] of Object.entries(changes)) {
                next[soHd] = { ...(next[soHd] ?? {}) };
                for (const [code, value] of Object.entries(codes)) next[soHd][code] = String(value);
            }
            return next;
        });
        setDialog(null);
        toast.info(`Đã đưa số của ${Object.keys(changes).length} nhân sự vào bảng, kiểm tra rồi bấm Lưu.`);
    };

    const code = createMode ? formatPayrollCode(month, year) : maKy;
    const exists = createMode && existingCodes.has(code);
    const conflicts = createMode ? draft?.resolved.conflicts ?? [] : [];
    const excluded = createMode ? draft?.resolved.excludedEmployees ?? [] : [];
    const draftWarnings = createMode ? [...(draft?.resolved.warnings ?? []), ...(draft?.ref.warnings ?? [])] : [];
    const canCreate = createMode && canManage && draft && !draftLoading && !exists && !conflicts.length && draft.resolved.employees.length > 0 && !invalidCells.size;
    const visibleTemplates = (templateList ?? []).filter((template) => matchesSearch(`${template.MA_MAU} ${template.TEN_MAU}`, templateSearch));
    const toggleTemplate = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    const excludedGroups = Object.entries(excluded.reduce((result, item) => ({ ...result, [item.reasonCode]: [...(result[item.reasonCode] ?? []), item] }), {}));

    return (
        <div className="p-3 bg-gray-50 min-h-screen space-y-3">
            {/* Thanh trên: tiêu đề, tóm tắt, quy trình, thao tác */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <button type="button" onClick={handleBack} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700" title="Quay lại danh sách" aria-label="Quay lại"><ArrowLeft className="w-4 h-4" /></button>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-2xl font-bold text-gray-800">{createMode ? 'Tạo bảng lương tháng' : `Bảng lương tháng ${period?.THANG ?? ''}/${period?.NAM ?? ''}`}</h1>
                            {period && !createMode && <Badge tone={STATUS_TONES[period.TRANG_THAI]}>{PAYROLL_STATUS_LABELS[period.TRANG_THAI]}</Badge>}
                            {periodEditable && (createMode || stale || !period?.CALCULATED_AT || dirtyCount > 0) && (
                                <Badge tone="blue" title="Số trên bảng tính tạm theo dữ liệu hiện tại; bấm Chốt số để ghi số chính thức">Số tạm tính</Badge>
                            )}
                            {dirtyCount > 0 && <Badge tone="yellow" title="Bấm Lưu hoặc Chốt số để ghi lại">{dirtyCount} ô chưa lưu</Badge>}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                            <span>{code} · {view?.details.length ?? 0} nhân sự · Thực chi {formatMoney(summary.totalNet) || 0} đ{periodEditable ? ' (tạm tính)' : ''}{period?.CALCULATED_AT ? ` · Chốt số lúc ${formatDateTime(period.CALCULATED_AT)}` : ''}</span>
                            {period && !createMode && <WorkflowSteps period={period} stale={stale} />}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {createMode ? (
                        <button className={buttonPrimary} disabled={!canCreate || Boolean(busy)} onClick={handleCreate}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Tạo bảng lương
                        </button>
                    ) : (
                        <>
                            {editable && (
                                <>
                                    {dirtyCount > 0 && <button className={buttonSecondary} onClick={() => setPendingRaw({})} disabled={Boolean(busy)}><Undo2 className="w-4 h-4" />Hủy sửa</button>}
                                    <button className={buttonSecondary} onClick={() => setDialog({ type: 'import' })} disabled={Boolean(busy) || !groups.length}><FileSpreadsheet className="w-4 h-4" />Nhập Excel</button>
                                    <button className={buttonSecondary} onClick={handleSave} disabled={Boolean(busy) || !dirtyCount}><Save className="w-4 h-4" />Lưu{dirtyCount ? ` (${dirtyCount})` : ''}</button>
                                    <button className={buttonSecondary} onClick={handleCalculate} disabled={Boolean(busy)}><Calculator className="w-4 h-4" />Chốt số</button>
                                    <button className={buttonPrimary} onClick={handleSubmit} disabled={Boolean(busy)}><Send className="w-4 h-4" />Gửi duyệt</button>
                                </>
                            )}
                            {canManage && period?.TRANG_THAI === 'CHO_DUYET' && (
                                <>
                                    <button className={buttonPrimary} onClick={() => setDialog({ type: 'approve' })} disabled={Boolean(busy) || period.NGUOI_TAO === user}
                                        title={period.NGUOI_TAO === user ? 'Người lập không được tự duyệt' : undefined}><CheckCircle2 className="w-4 h-4" />Duyệt</button>
                                    <button className={buttonSecondary} onClick={() => setDialog({ type: 'reject' })} disabled={Boolean(busy)}><XCircle className="w-4 h-4" />Từ chối</button>
                                </>
                            )}
                            {canManage && period?.TRANG_THAI === 'DA_DUYET' && (
                                <button className={buttonSecondary} onClick={() => setDialog({ type: 'reopen' })} disabled={Boolean(busy)}><RotateCcw className="w-4 h-4" />Mở lại</button>
                            )}
                            <MoreMenu items={[
                                groups.length && { label: 'Xuất Excel', icon: Download, onClick: () => exportPayrollWorkbook(period, groups, { draft: period.TRANG_THAI !== 'DA_DUYET' }).catch((error) => toast.error(error.message)) },
                                { label: 'In bảng lương', icon: Printer, onClick: () => window.open(`/in/bangcongluong/${maKy}`, '_blank') },
                                editable && { label: 'Đổi mẫu / cập nhật nhân sự', icon: Users, onClick: handleChangeTemplates },
                                { label: 'Nhật ký thao tác', icon: ScrollText, onClick: handleShowAudit },
                                editable && { label: 'Xóa kỳ lương', icon: Trash, danger: true, onClick: () => setDialog({ type: 'delete' }) },
                            ]} />
                        </>
                    )}
                </div>
            </div>

            <div className="flex gap-3 items-start">
                {/* Khung trái: thông tin bảng lương */}
                {panelOpen && (
                    <aside className="w-72 flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-5 max-h-[calc(100vh-11rem)] overflow-y-auto sticky top-3">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-gray-800">Thông tin bảng lương</p>
                            <button type="button" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" onClick={() => setPanelOpen(false)} title="Thu gọn" aria-label="Thu gọn"><PanelLeftClose className="w-4 h-4" /></button>
                        </div>
                        <PanelSection title="Lọc bảng lương">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input className={`${inputClass} pl-8`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm mã hoặc tên nhân sự..." aria-label="Tìm nhân sự trong bảng lương" />
                            </div>
                            {(teams.length > 0 || hasNoTeam) && (
                                <select className={inputClass} value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} aria-label="Lọc theo tổ">
                                    <option value="ALL">Tất cả tổ</option>
                                    {hasNoTeam && <option value={NO_TEAM}>Chưa phân tổ</option>}
                                    {teams.map((team) => <option key={team} value={team}>{team}</option>)}
                                </select>
                            )}
                            {warningRows > 0 && (
                                <button type="button" onClick={() => setOnlyWarnings(!onlyWarnings)}
                                    className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 ${onlyWarnings ? 'ring-2 ring-yellow-500' : ''}`}>
                                    <AlertTriangle className="w-3.5 h-3.5" />{onlyWarnings ? 'Đang chỉ hiện' : 'Chỉ hiện'} {warningRows} người có cảnh báo
                                </button>
                            )}
                        </PanelSection>
                        <PanelSection title="Chú thích">
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                                {legendSources.map((source) => {
                                    const meta = SOURCE_BADGES[source] ?? SOURCE_BADGES.HE_THONG;
                                    return <span key={source} className="inline-flex items-center gap-1 text-xs text-gray-600"><span className={`h-4 px-1 rounded text-[10px] font-bold leading-4 ${meta.className}`}>{meta.short}</span>{meta.label}</span>;
                                })}
                            </div>
                            <div className="space-y-1 text-xs text-gray-600 border-t border-gray-100 pt-2">
                                <p className="flex items-center gap-1.5"><span className="inline-block w-8 h-4 rounded border border-gray-300 bg-white flex-shrink-0" />Ô có khung: nhập trực tiếp, Enter xuống người kế tiếp</p>
                                <p className="flex items-center gap-1.5"><span className="px-1 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold">CT</span>Bấm trên tiêu đề cột để soi công thức</p>
                            </div>
                        </PanelSection>
                        <PanelSection title="Kỳ lương">
                            <div className="grid grid-cols-2 gap-2">
                                <select className={inputClass} value={createMode ? month : period?.THANG ?? ''} disabled={!createMode} onChange={(e) => setMonth(Number(e.target.value))} aria-label="Tháng">
                                    {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                                </select>
                                <input className={inputClass} value={createMode ? year : period?.NAM ?? ''} disabled={!createMode} onChange={(e) => setYear(Number(e.target.value) || today.getFullYear())} inputMode="numeric" aria-label="Năm" />
                            </div>
                            {exists && <MessageList items={[`Kỳ lương ${code} đã tồn tại.`]} />}
                        </PanelSection>
                        <PanelSection title={createMode ? `Mẫu bảng lương áp dụng (${selected.length})` : 'Mẫu bảng lương áp dụng'}>
                            {createMode ? (
                                !templateList ? <Spinner label="Đang tải mẫu..." /> : (
                                    <>
                                        <input className={inputClass} value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)} placeholder="Tìm mẫu..." />
                                        <div className="space-y-1">
                                            {visibleTemplates.map((template) => {
                                                const isSelected = selected.includes(template.MA_MAU);
                                                const count = draft?.resolved.employees.filter((employee) => employee.templateId === template.MA_MAU).length;
                                                return (
                                                    <button key={template.MA_MAU} type="button" onClick={() => toggleTemplate(template.MA_MAU)}
                                                        className={`w-full flex items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${isSelected ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-gray-50 text-gray-700'}`}>
                                                        <span className={`mt-0.5 w-4 h-4 flex-shrink-0 flex items-center justify-center rounded border-2 ${isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>{isSelected && <Check className="w-3 h-3 text-white" />}</span>
                                                        <span className="flex-1"><span className="block font-medium">{template.TEN_MAU}</span><span className="block text-xs text-gray-500 font-mono">{template.MA_MAU}</span></span>
                                                        {isSelected && count !== undefined && <span className="text-xs text-gray-500 flex items-center gap-0.5"><Users className="w-3 h-3" />{count}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )
                            ) : (
                                <div className="space-y-1">
                                    {usedTemplates.map((item) => (
                                        <div key={item.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-2.5 py-2">
                                            <div><p className="text-sm font-medium text-gray-800">{item.name}</p><p className="text-xs text-gray-500 font-mono">{item.id}</p></div>
                                            <span className="text-xs text-gray-500 flex items-center gap-0.5"><Users className="w-3 h-3" />{item.count}</span>
                                        </div>
                                    ))}
                                    {editable && <button type="button" className={`${buttonSecondary} w-full justify-center`} onClick={handleChangeTemplates} disabled={Boolean(busy)}><Layers3 className="w-4 h-4" />Đổi mẫu áp dụng</button>}
                                </div>
                            )}
                        </PanelSection>
                        <PanelSection title="Ghi chú">
                            {createMode
                                ? <textarea rows={2} className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú chung cho bảng lương..." />
                                : editable ? (
                                    <>
                                        <textarea rows={2} className={inputClass} value={noteDraft ?? period?.GHI_CHU ?? ''} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Ghi chú chung cho bảng lương..." />
                                        {noteDraft !== null && noteDraft !== (period?.GHI_CHU ?? '') && (
                                            <div className="flex gap-2">
                                                <button type="button" className={buttonPrimary} onClick={handleSaveNote} disabled={Boolean(busy)}><Save className="w-4 h-4" />Lưu ghi chú</button>
                                                <button type="button" className={buttonSecondary} onClick={() => setNoteDraft(null)}>Hủy</button>
                                            </div>
                                        )}
                                    </>
                                ) : <p className="text-sm text-gray-700">{period?.GHI_CHU || <span className="text-gray-400">Không có</span>}</p>}
                        </PanelSection>
                        {!createMode && period && (
                            <PanelSection title="Quy trình">
                                <div className="text-xs text-gray-600 space-y-1">
                                    <p>Người lập: <b>{period.NGUOI_TAO || '—'}</b></p>
                                    {period.NGUOI_GUI && <p>Gửi duyệt: <b>{period.NGUOI_GUI}</b> lúc {formatDateTime(period.NGAY_GUI)}</p>}
                                    {period.NGUOI_DUYET && period.TRANG_THAI === 'DA_DUYET' && <p>Duyệt: <b>{period.NGUOI_DUYET}</b> lúc {formatDateTime(period.NGAY_DUYET)}</p>}
                                </div>
                                {period.TRANG_THAI === 'TU_CHOI' && period.LY_DO_TU_CHOI && <MessageList items={[`Bị từ chối bởi ${period.NGUOI_DUYET}: ${period.LY_DO_TU_CHOI}`]} />}
                                {period.LY_DO_MO_LAI && period.TRANG_THAI === 'NHAP' && <MessageList tone="blue" items={[`Mở lại bởi ${period.NGUOI_MO_LAI}: ${period.LY_DO_MO_LAI}`]} />}
                            </PanelSection>
                        )}
                        {(createMode ? draftWarnings.length > 0 : periodEditable) && (
                            <PanelSection title="Cảnh báo">
                                {createMode ? draftWarnings.map((warning) => {
                                    const missing = (warning.templateIds ?? []).filter((id) => !selected.includes(id));
                                    return (
                                        <div key={warning.code + warning.message} className={`rounded-lg p-3 text-xs ${warning.blocking ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-800'}`}>
                                            {warning.message}
                                            {missing.length > 0 && <button type="button" className="block mt-2 font-semibold underline" onClick={() => setSelected((prev) => [...new Set([...prev, ...missing])])}>Chọn thêm {missing.length} mẫu này</button>}
                                        </div>
                                    );
                                }) : (
                                    <>
                                        <MessageList items={wb.readiness.blocking} />
                                        <MessageList tone="yellow" items={wb.readiness.warnings} />
                                        {!wb.readiness.blocking.length && <MessageList tone="green" items={['Số đã chốt khớp dữ liệu hiện tại, có thể gửi duyệt.']} />}
                                    </>
                                )}
                            </PanelSection>
                        )}
                        {conflicts.length > 0 && (
                            <PanelSection title={`Xung đột mẫu (${conflicts.length})`}>
                                {conflicts.map((conflict) => (
                                    <div key={conflict.employeeId} className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs">
                                        <p className="font-semibold text-red-800">{conflict.employeeName} ({conflict.employeeCode})</p>
                                        <p className="text-red-700">Trùng mẫu cùng cấp: {conflict.templates.map((template) => template.templateName).join(', ')}</p>
                                        {canManage && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {conflict.templates.map((template) => (
                                                    <button key={template.templateId} type="button" disabled={Boolean(busy)} onClick={() => handleExcludeFromTemplate(template.templateId, template.templateName, conflict)}
                                                        className="px-2 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100">Gỡ khỏi {template.templateName}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </PanelSection>
                        )}
                        {excluded.length > 0 && (
                            <details className="text-xs">
                                <summary className="cursor-pointer font-semibold text-gray-700">{excluded.length} nhân sự được bỏ qua</summary>
                                <div className="mt-2 space-y-2">
                                    {excludedGroups.map(([reasonCode, people]) => (
                                        <div key={reasonCode}>
                                            <p className="font-medium text-gray-800">{PAYROLL_EXCLUSION_REASONS[reasonCode] || reasonCode} ({people.length})</p>
                                            <p className="text-gray-500">{people[0].reason}</p>
                                            <div className="mt-1 space-y-0.5 text-gray-600">{people.map((person) => <p key={person.employeeId}>{person.employeeName} · {person.employeeCode}{person.team ? ` · ${person.team}` : ''}</p>)}</div>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}
                    </aside>
                )}

                {/* Khu chính: tab tổng hợp + mỗi mẫu một bảng nhập */}
                <main className="flex-1 min-w-0 bg-white rounded-xl shadow-sm border border-gray-100 p-4 relative">
                    {busy && (
                        <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/70 rounded-xl">
                            <div className="flex flex-col items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-xl p-6 text-center max-w-sm">
                                <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                                <p className="font-semibold text-gray-800">{busy}</p>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-2 mb-3 border-b border-gray-200">
                    <div className="flex items-center gap-1 overflow-x-auto overflow-y-hidden -mb-px">
                        {!panelOpen && <button type="button" className="p-1.5 mr-1 rounded-lg text-gray-500 hover:bg-gray-100" onClick={() => setPanelOpen(true)} title="Mở thông tin bảng lương" aria-label="Mở khung thông tin"><PanelLeftOpen className="w-4 h-4" /></button>}
                        {[{ templateId: SUMMARY_TAB, templateName: 'Tổng hợp', rows: filteredGroups.flatMap((group) => group.rows) }, ...filteredGroups].map((item) => (
                            <button key={item.templateId} type="button" onClick={() => setActiveTab(item.templateId)}
                                className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 flex items-center gap-1.5 ${item.templateId === currentTab ? 'border-indigo-600 text-indigo-700 font-semibold' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>
                                {item.templateName}<Badge tone={item.templateId === currentTab ? 'indigo' : 'gray'}>{item.rows.length}</Badge>
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pb-2">
                        {currentTab !== SUMMARY_TAB && (
                            <>
                                <div className="flex rounded-lg border border-gray-300 bg-white p-0.5">
                                    {[[false, 'Tất cả biến'], [true, 'Chỉ ô nhập']].map(([value, label]) => (
                                        <button key={label} type="button" onClick={() => setShowOnlyInputs(value)}
                                            className={`px-3 py-1 rounded-md text-xs ${showOnlyInputs === value ? 'bg-indigo-100 text-indigo-800 font-semibold' : 'text-gray-600 hover:text-gray-800'}`}>{label}</button>
                                    ))}
                                </div>
                                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                    <button type="button" title="Xếp ngang theo cột" aria-label="Xếp ngang theo cột" onClick={() => changeLayout('horizontal')}
                                        className={`p-1.5 ${layout === 'horizontal' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}><Rows3 className="w-4 h-4" /></button>
                                    <button type="button" title="Xếp dọc theo nhóm biến" aria-label="Xếp dọc theo nhóm biến" onClick={() => changeLayout('vertical')}
                                        className={`p-1.5 ${layout === 'vertical' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}><LayoutList className="w-4 h-4" /></button>
                                </div>
                            </>
                        )}
                        <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sắp xếp">
                            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        {editable && !createMode && activeUsesProductivity && (
                            <button type="button" className={buttonSecondary} onClick={handleLoadProductivity} disabled={Boolean(busy)}
                                title="Nạp sản lượng của kỳ vào bảng để xem lương sản phẩm ngay. Chốt số vẫn tự nạp lại."><Factory className="w-4 h-4" />Nạp năng suất</button>
                        )}
                    </div>
                    </div>
                    {(createMode ? (!templateList || (draftLoading && !draft)) : !wb) ? <Spinner label={createMode ? 'Đang lập danh sách nhân sự và mẫu áp dụng...' : 'Đang tải kỳ lương và dữ liệu nguồn...'} /> : (
                        <>
                            {createMode && !selected.length && <MessageList tone="yellow" items={['Chọn ít nhất một mẫu bảng lương ở khung bên trái.']} />}
                            {createMode && draft && <div className="mb-3"><MessageList tone="blue" items={['Bảng tạm tính chưa có lương sản phẩm; năng suất được nạp khi chốt số. Có thể nhập công ngay, số được ghi cùng lúc tạo bảng lương.']} /></div>}
                            {live?.error && <MessageList items={[live.error]} />}
                            {!periodEditable && stale && <div className="mb-3"><MessageList tone="yellow" items={['Dữ liệu nguồn đã thay đổi sau khi chốt. Bảng vẫn hiện số đã chốt.']} /></div>}
                            {periodEditable && !createMode && dirtyCount > 0 && <div className="mb-3"><MessageList tone="blue" items={[`Có ${dirtyCount} ô đang sửa chưa lưu; số trên bảng đã tính tạm theo các ô này.`]} /></div>}
                            {currentTab === SUMMARY_TAB ? (
                                groups.length ? <SummaryTable groups={filteredGroups} sort={sort} onView={(row) => setDialog({ type: 'detail', row, group: groups.find((group) => group.rows.some((item) => item.soHd === row.soHd)) })} />
                                    : !live?.error && <MessageList tone="yellow" items={[createMode ? 'Chưa có nhân sự nào thuộc các mẫu đã chọn.' : 'Kỳ lương chưa có nhân sự. Dùng Cập nhật nhân sự để thêm.']} />
                            ) : activeGroup && (
                                <WorkbenchMatrix group={activeGroup} rows={activeRows} pendingRaw={pendingRaw} invalidCells={invalidCells}
                                    layout={layout} showOnlyInputs={showOnlyInputs} onInputChange={handleInputChange}
                                    onRowClick={(row) => setDialog({ type: 'detail', row, group: activeGroup })} />
                            )}
                        </>
                    )}
                </main>
            </div>
            {dialog?.type === 'detail' && (
                <EmployeeDetailModal row={dialog.row} group={dialog.group} detail={detailBySoHd.get(dialog.row.soHd)} refData={view.ref}
                    useSnapshot={!periodEditable} onClose={() => setDialog(null)} />
            )}
            {dialog?.type === 'calculate' && (
                <CalculationPreviewModal preview={dialog.preview} previous={period} onClose={() => setDialog(null)}
                    onConfirm={async () => {
                        const result = await run('Đang chốt số...', () => confirmPeriodCalculation(maKy, dialog.preview));
                        if (result) { setDialog(null); toast.success(`Đã chốt số: tổng thực chi ${formatMoney(result.totalNet)} đ`); }
                    }} />
            )}
            {dialog?.type === 'confirmWarnings' && (
                <ConfirmWarningsModal warnings={dialog.warnings} onClose={() => setDialog(null)}
                    onConfirm={async () => {
                        const result = await run('Đang gửi duyệt...', () => submitPeriod(maKy, true));
                        if (result?.submitted) { setDialog(null); toast.success('Đã gửi kỳ lương chờ duyệt'); }
                    }} />
            )}
            {dialog?.type === 'approve' && (
                <ConfirmDialog title="Duyệt kỳ lương" danger={false} confirmLabel="Duyệt" onClose={() => setDialog(null)}
                    message={<>Duyệt kỳ lương <b>{maKy}</b>? Phiếu ứng của kỳ sẽ chuyển sang đã khấu trừ và các mẫu đã dùng bị khóa.</>}
                    onConfirm={async () => {
                        const ok = await run('Đang duyệt...', async () => { await approvePeriod(maKy); return true; });
                        if (ok) { setDialog(null); toast.success('Đã duyệt kỳ lương'); }
                    }} />
            )}
            {dialog?.type === 'reject' && (
                <ReasonModal title="Từ chối bảng lương" label="Lý do từ chối" confirmLabel="Từ chối" onClose={() => setDialog(null)}
                    onSubmit={async (reason) => {
                        const ok = await run('Đang từ chối...', async () => { await rejectPeriod(maKy, reason); return true; });
                        if (ok) { setDialog(null); toast.success('Đã từ chối kỳ lương'); }
                    }} />
            )}
            {dialog?.type === 'reopen' && (
                <ReasonModal title="Mở lại bảng lương" label="Lý do mở lại (phiếu ứng được trả lại, số đã chốt bị xóa)" confirmLabel="Mở lại" onClose={() => setDialog(null)}
                    onSubmit={async (reason) => {
                        const ok = await run('Đang mở lại...', async () => { await reopenPeriod(maKy, reason); return true; });
                        if (ok) { setDialog(null); toast.success('Đã mở lại kỳ lương về Nháp'); }
                    }} />
            )}
            {dialog?.type === 'delete' && (
                <ConfirmDialog title="Xác nhận xóa kỳ lương" confirmLabel="Xóa kỳ" confirmText={maKy} onClose={() => setDialog(null)}
                    message="Xóa kỳ lương và toàn bộ số đã nhập. Thao tác không hoàn tác được."
                    onConfirm={async () => {
                        const ok = await run('Đang xóa...', async () => { await deletePeriod(maKy); return true; }, { saveFirst: false, reloadAfter: false });
                        if (ok) { toast.success('Đã xóa kỳ lương'); navigate('/bangcongluong'); }
                    }} />
            )}
            {dialog?.type === 'leave' && (
                <ConfirmDialog title="Rời khỏi bảng lương?" confirmLabel="Bỏ thay đổi và rời đi" onClose={() => setDialog(null)}
                    message={`Còn ${dirtyCount} ô chưa lưu. Rời khỏi trang sẽ mất các số vừa nhập.`}
                    onConfirm={() => { setPendingRaw({}); navigate('/bangcongluong'); }} />
            )}
            {dialog?.type === 'import' && <ImportInputsModal period={period} groups={groups} details={view.details} onClose={() => setDialog(null)} onApply={applyImportedChanges} />}
            {dialog?.type === 'templates' && (
                <ChangeTemplatesModal period={period} templates={dialog.templates} onClose={() => setDialog(null)}
                    onPreview={(codes) => previewPeriodEmployeeRefresh(maKy, codes)}
                    onApply={async (codes) => {
                        const diff = await run('Đang cập nhật mẫu và nhân sự...', () => applyPeriodEmployeeRefresh(maKy, codes));
                        if (diff) { setDialog(null); setActiveTab(null); toast.success(`Đã thêm ${diff.added.length}, bỏ ${diff.removed.length}, chuyển mẫu ${diff.changed.length} người`); }
                    }} />
            )}
            {dialog?.type === 'audit' && <AuditModal rows={dialog.rows} onClose={() => setDialog(null)} />}
            {createMode && canManage && !draftLoading && draft && conflicts.length > 0 && (
                <div className="fixed bottom-4 right-4 z-30 bg-white border border-red-200 shadow-lg rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />Còn {conflicts.length} xung đột mẫu, xử lý ở khung bên trái trước khi tạo.
                </div>
            )}
        </div>
    );
};

export default BangCongLuongChiTiet;
